// The fill-in field as an editor node: an inline, atomic chip that carries
// its raw placeholder ("{{date|Follow-up date}}") and opens a small filler
// when clicked. Filling it replaces the chip with plain text; removing it
// deletes the chip. See docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md §5.2 C11.
import { useEffect, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import * as Popover from '@radix-ui/react-popover';
import { AlertTriangle, CalendarDays, Hash, ListChecks, Type } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import { DateFieldPicker } from './DateFieldPicker';
import { innerOf, parseSmartField, type SmartFieldKind } from './smartFields';

const ICONS: Record<SmartFieldKind, typeof Type> = {
  date: CalendarDays,
  text: Type,
  number: Hash,
  choice: ListChecks,
};

function SmartFieldView({ node, editor, getPos }: NodeViewProps) {
  const raw = String(node.attrs.raw ?? '');
  const field = parseSmartField(innerOf(raw));
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  // `editor.isEditable` toggles (e.g. while an AI reply streams into the
  // note) via `editor.setEditable()` + a meta-only transaction with no doc
  // change — ProseMirror then has nothing to compare for this node (same
  // content, no decorations touching it) and skips re-rendering this node
  // view, leaving the button disabled forever after the first toggle. This
  // state is reactive to every transaction the editor fires, independent of
  // whether the node view itself gets re-rendered, so the button never gets
  // stuck mid-generation.
  const [editable, setEditable] = useState(editor.isEditable);
  useEffect(() => {
    const sync = () => setEditable(editor.isEditable);
    sync();
    editor.on('transaction', sync);
    return () => { editor.off('transaction', sync); };
  }, [editor]);

  const range = () => {
    const pos = typeof getPos === 'function' ? getPos() : undefined;
    return typeof pos === 'number' ? { from: pos, to: pos + node.nodeSize } : null;
  };

  const fill = (text: string) => {
    const r = range();
    if (!r) return;
    const chain = editor.chain().focus();
    if (text) chain.insertContentAt(r, text).run();
    else chain.deleteRange(r).run();
    setOpen(false);
  };

  const Icon = field ? ICONS[field.kind] : AlertTriangle;
  const numberText = () => {
    if (!field?.unit) return value.trim();
    return field.unit === '%' ? `${value.trim()}%` : `${value.trim()} ${field.unit}`;
  };

  return (
    <NodeViewWrapper as="span" className="mx-0.5 inline-block align-baseline">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild disabled={!editable}>
          <button
            type="button"
            contentEditable={false}
            title={field ? `Fill in: ${field.label}` : 'Unrecognised placeholder — remove it before approving'}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-px text-[12px] font-medium leading-5',
              field ? 'border-warn bg-warn-soft text-warn-ink' : 'border-danger bg-danger-soft text-danger-ink'
            )}
          >
            <Icon className="h-3 w-3" aria-hidden="true" />
            {field ? field.label : raw}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            sideOffset={6}
            align="start"
            className="z-50 rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-md"
          >
            {field?.kind === 'date' && <DateFieldPicker label={field.label} onPick={fill} />}

            {field?.kind === 'text' && (
              <form className="flex w-72 flex-col gap-2" onSubmit={(e) => { e.preventDefault(); if (value.trim()) fill(value.trim()); }}>
                <div className="text-xs font-semibold text-ink">{field.label}</div>
                <Textarea rows={3} value={value} autoFocus onChange={(e) => setValue(e.target.value)} />
                <Button type="submit" size="sm" disabled={!value.trim()}>Insert</Button>
              </form>
            )}

            {field?.kind === 'number' && (
              <form className="flex w-56 flex-col gap-2" onSubmit={(e) => { e.preventDefault(); if (value.trim()) fill(numberText()); }}>
                <div className="text-xs font-semibold text-ink">{field.label}</div>
                <div className="flex items-center gap-2">
                  <Input type="number" inputMode="decimal" step="any" value={value} autoFocus onChange={(e) => setValue(e.target.value)} />
                  {field.unit && <span className="text-xs text-ink-muted">{field.unit}</span>}
                </div>
                <Button type="submit" size="sm" disabled={!value.trim()}>Insert</Button>
              </form>
            )}

            {field?.kind === 'choice' && (
              <div className="flex w-64 flex-col gap-2">
                <div className="text-xs font-semibold text-ink">{field.label}</div>
                <div className="flex flex-wrap gap-1">
                  {field.options!.map((option) => (
                    <Button key={option} type="button" size="sm" variant="ghost" onClick={() => fill(option)}>{option}</Button>
                  ))}
                </div>
              </div>
            )}

            {!field && (
              <div className="w-64 text-xs text-ink-muted">
                This placeholder isn&apos;t a recognised fill-in field. Remove it, or type the text it should contain.
              </div>
            )}

            <div className="mt-2 border-t border-border pt-2">
              <Button type="button" size="sm" variant="link" onClick={() => fill('')}>Remove this field</Button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </NodeViewWrapper>
  );
}

export const SmartFieldExtension = Node.create({
  name: 'smartField',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return { raw: { default: '' } };
  },

  parseHTML() {
    return [{
      tag: 'span[data-smart-field]',
      getAttrs: (element) => ({ raw: (element as HTMLElement).getAttribute('data-raw') ?? '' }),
    }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-smart-field': '', 'data-raw': node.attrs.raw }), node.attrs.raw];
  },

  // Copying a chip out of the editor as plain text yields the placeholder itself.
  renderText({ node }) {
    return String(node.attrs.raw ?? '');
  },

  addNodeView() {
    return ReactNodeViewRenderer(SmartFieldView);
  },
});
